import { Link } from 'react-router';
import { ArrowLeft, ICONO } from '../ui/iconos.js';

/**
 * El encabezado de un flujo del paciente (app-header): volver, título y, si
 * corresponde, en qué paso va.
 *
 * Con paso son tres renglones (E3.11): volver y el paso, el título a todo el
 * ancho, y los segmentos de progreso, que pone el formulario debajo.
 *
 * El botón de volver es un enlace (lleva a otra pantalla) de 48 px con su nombre
 * accesible, porque el ícono es lo único que lo distingue (D-128). El título es
 * el `h1` de la pantalla. El paso va en texto y en una región `status`, que es lo
 * que se anuncia cuando cambia.
 */
export function EncabezadoDeFlujo({
  titulo,
  volverA,
  etiquetaDeVolver,
  paso,
}: {
  titulo: string;
  /** A dónde lleva el botón de volver. Sin destino, no hay botón. */
  volverA?: string | undefined;
  etiquetaDeVolver?: string | undefined;
  paso?: string | undefined;
}) {
  const volver = volverA ? (
    <Link
      aria-label={etiquetaDeVolver}
      className="flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-soft text-link"
      to={volverA}
    >
      <ArrowLeft aria-hidden="true" className={ICONO.normal} />
    </Link>
  ) : null;

  // Con paso (un formulario), el título va en su propio renglón y a todo el
  // ancho: compartiendo renglón con volver y con "Pregunta 6 de 6", un título
  // largo se rompía (E3.11). Arriba, volver a la izquierda y el paso a la
  // derecha; abajo, el título, que puede ocupar dos líneas.
  if (paso) {
    return (
      <header className="flex shrink-0 flex-col gap-3 bg-card px-5 pt-5 pb-3">
        <div className="flex min-h-12 items-center justify-between gap-3">
          {volver ?? <span aria-hidden="true" />}
          <p className="m-0 text-base font-medium text-subtle" role="status">
            {paso}
          </p>
        </div>
        <h1 className="m-0 text-lg leading-tight font-bold text-balance text-heading">{titulo}</h1>
      </header>
    );
  }

  return (
    <header className="flex shrink-0 items-center gap-3 bg-card px-5 pt-5 pb-3">
      {volver}
      <h1 className="m-0 flex-1 text-lg font-bold text-heading">{titulo}</h1>
    </header>
  );
}
